import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatCardModule } from '@angular/material/card';

import { RiskSummary, RiskSummaryField } from '../../core/df/df.types';
import { IconComponent, IconName } from '../icon/icon';
import { StatusChipComponent } from '../status-chip/status-chip';
import { ZarPipe } from '../pipes/zar.pipe';
import { DashPipe } from '../pipes/dash.pipe';

/**
 * Product-shaped risk lens for an Ops reviewer (Ops-only surface; N4).
 *
 * Kickstart leads with cash flow ("cash flow is king"); Debtors Finance leads
 * with age analysis / insurability / concentration. The backend groups the
 * already-captured signals into a {@link RiskSummary}; this panel renders them,
 * marking Phase-2-dormant signals as "Pending" rather than inventing values.
 * Dumb component — pass `riskSummary` from the ops detail.
 */
@Component({
  selector: 'app-risk-summary-panel',
  imports: [MatCardModule, IconComponent, StatusChipComponent, ZarPipe, DashPipe],
  templateUrl: './risk-summary-panel.html',
  styleUrl: './risk-summary-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskSummaryPanelComponent {
  readonly riskSummary = input.required<RiskSummary>();

  protected readonly heading = computed(() =>
    this.riskSummary().lens === 'cash_flow'
      ? 'Risk summary — cash flow'
      : 'Risk summary — age analysis',
  );

  protected readonly icon = computed<IconName>(() =>
    this.riskSummary().lens === 'cash_flow' ? 'trending-up' : 'inbox',
  );

  protected isPending(field: RiskSummaryField): boolean {
    return field.pending === true || field.value === null || field.value === '';
  }

  /** True when any field is pending — gates the Phase-1 explanatory footnote. */
  protected readonly hasPending = computed(() =>
    this.riskSummary().fields.some((f) => this.isPending(f)),
  );
}
